import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { UrlApi } from "../../../services/apirest";
import FileUploadEnvasado from "../../envasados/fileUpload/FileUploadEnvasado";
import PaymentsIcon from "@mui/icons-material/Payments";
import TarjetaDetalle from "./TarjetaDetalle";

// Cobros registrados desde la app de repartos de granel (tabla granel_pagos):
// - Cobro del pedido: pagos sin gestión de cuenta corriente.
// - Cuenta corriente: deudas del pedido (gestion_cc con monto) y los pagos con que se cobraron.
// Los pagos se agrupan por recibo (uno por cobro, aunque tenga varios medios).

const formatearMonto = (monto) =>
  `$${Number(monto || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const sumar = (pagos) => pagos.reduce((suma, pago) => suma + (Number(pago.importe) || 0), 0);

// Tipo de medio según su nombre (mismo criterio que la app)
const tipoMedio = (nombre = "") => {
  if (/posnet|point/i.test(nombre)) return "point";
  if (/\bqr\b|mercado ?pago/i.test(nombre)) return "qr";
  if (/efectivo/i.test(nombre)) return "efectivo";
  if (/cheque/i.test(nombre)) return "cheque";
  if (/transferencia/i.test(nombre)) return "transferencia";
  if (/tarjeta|d[eé]bito|cr[eé]dito/i.test(nombre)) return "tarjeta";
  return "otro";
};

// Uuid del pedido con el que se subió el comprobante (el archivo se guarda como <uuid>-<nombre>)
const uuidDeComprobante = (nombreArchivo = "") =>
  (nombreArchivo.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i) || [])[0] || null;

// Datos propios de cada medio de pago
function DatosPago({ pago, marcas, onVerComprobante }) {
  const marca = pago.tipo_tarjeta ? marcas[pago.tipo_tarjeta] || pago.tipo_tarjeta : null;
  const tarjeta = [
    marca,
    pago.ultimos_digitos ? `**** ${pago.ultimos_digitos}` : null,
  ];
  let partes;

  switch (tipoMedio(pago.medio)) {
    case "efectivo":
      partes = [];
      break;
    case "tarjeta":
      partes = [...tarjeta, pago.cupon ? `Cupón ${pago.cupon}` : null, pago.lote ? `Lote ${pago.lote}` : null];
      break;
    case "point":
      partes = [...tarjeta, pago.order_id ? `Orden MP ${pago.order_id}` : null];
      break;
    case "qr":
      partes = [
        pago.order_id ? `Orden MP ${pago.order_id}` : null,
        pago.payment_id ? `ID de pago ${pago.payment_id}` : null,
      ];
      break;
    case "cheque":
      partes = [
        pago.nro_cheque ? `N° ${pago.nro_cheque}` : null,
        pago.banco || null,
        pago.fecha_cheque ? `Vto. ${moment(pago.fecha_cheque).format("DD-MM-YYYY")}` : null,
      ];
      break;
    case "transferencia":
      partes = [pago.cuit_abona ? `CUIT/DNI ${pago.cuit_abona}` : null];
      break;
    default:
      partes = [
        ...tarjeta,
        pago.nro_cheque ? `Cheque ${pago.nro_cheque}` : null,
        pago.banco || null,
        pago.cuit_abona ? `CUIT/DNI ${pago.cuit_abona}` : null,
        pago.cupon ? `Cupón ${pago.cupon}` : null,
        pago.lote ? `Lote ${pago.lote}` : null,
        pago.order_id ? `Orden MP ${pago.order_id}` : null,
        pago.detalle || null,
      ];
  }

  const texto = partes.filter(Boolean).join(" · ");

  return (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
      <Typography variant="body2">{texto || "-"}</Typography>
      {pago.comprobante && uuidDeComprobante(pago.comprobante) ? (
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={() => onVerComprobante(uuidDeComprobante(pago.comprobante))}
        >
          Ver comprobante
        </Button>
      ) : null}
    </Stack>
  );
}

// Agrupa los pagos por recibo, en el orden en que se registraron
const agruparPorRecibo = (pagos) => {
  const grupos = [];
  [...pagos]
    .sort((a, b) => a.id - b.id)
    .forEach((pago) => {
      const recibo = pago.nro_recibo || null;
      let grupo = grupos.find((g) => g.recibo === recibo);
      if (!grupo) {
        grupo = { recibo, pagos: [] };
        grupos.push(grupo);
      }
      grupo.pagos.push(pago);
    });
  return grupos;
};

function TablaPagos({ pagos, marcas, onVerComprobante }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Medio</TableCell>
            <TableCell align="right">Importe</TableCell>
            <TableCell>Datos</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agruparPorRecibo(pagos).map((grupo) => {
            const primero = grupo.pagos[0];
            return (
              <React.Fragment key={grupo.recibo || "sin-recibo"}>
                <TableRow sx={{ backgroundColor: "#F4F6F7" }}>
                  <TableCell colSpan={3}>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <ReceiptLongIcon fontSize="small" color={grupo.recibo ? "primary" : "disabled"} />
                      <Typography variant="body2" fontWeight="bold">
                        {grupo.recibo ? `Recibo ${grupo.recibo}` : "Sin recibo"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {primero.created_at ? moment(primero.created_at).format("DD-MM-YYYY HH:mm") : ""}
                        {primero.usuario ? ` · ${primero.usuario}` : ""}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ ml: "auto !important" }}>
                        Total {formatearMonto(sumar(grupo.pagos))}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
                {grupo.pagos.map((pago) => (
                  <TableRow key={pago.id}>
                    <TableCell>{pago.medio || "-"}</TableCell>
                    <TableCell align="right">{formatearMonto(pago.importe)}</TableCell>
                    <TableCell>
                      <DatosPago pago={pago} marcas={marcas} onVerComprobante={onVerComprobante} />
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Sección de cobros con el mismo estilo de tarjeta que el resto del detalle
function Seccion({ titulo, extra, children }) {
  return (
    <TarjetaDetalle icono={<PaymentsIcon fontSize="small" />} titulo={titulo} extra={extra} sx={{ mt: 1.5, height: "auto" }}>
      {children}
    </TarjetaDetalle>
  );
}

// Nombre de cada marca de tarjeta por su código (id_mp), para mostrar "Visa Débito" en vez de "debvisa"
const cargarMarcas = async () => {
  const marcas = {};
  await Promise.all(
    ["debito", "credito"].map(async (tipo) => {
      try {
        const response = await fetch(`${UrlApi}/envasado_medios_pago/tarjetas/${tipo}`);
        const data = await response.json();
        if (Array.isArray(data)) {
          data.forEach((marca) => {
            if (marca.id_mp) marcas[marca.id_mp] = marca.marca;
          });
        }
      } catch (e) {
        // Sin la lista se muestra el código tal cual
      }
    })
  );
  return marcas;
};

function CobrosPedido({ idPedido }) {
  const [pagos, setPagos] = useState(null);
  const [deudas, setDeudas] = useState(null);
  const [marcas, setMarcas] = useState({});
  //Comprobantes de transferencia: se ven con el mismo diálogo que envasado (archivos del pedido por uuid)
  const [comprobanteUid, setComprobanteUid] = useState(null);
  const [error, setError] = useState(null);

  const cargar = async () => {
    setPagos(null);
    setDeudas(null);
    setError(null);
    try {
      const [respPagos, respDeudas] = await Promise.all([
        fetch(`${UrlApi}/granel_pagos/xpedido/${idPedido}`),
        fetch(`${UrlApi}/granel_pagos/gestiones/${idPedido}`),
      ]);
      const dataPagos = await respPagos.json();
      const dataDeudas = await respDeudas.json();
      if (!respPagos.ok || !respDeudas.ok || !Array.isArray(dataPagos) || !Array.isArray(dataDeudas)) {
        throw new Error("Respuesta inválida");
      }
      setPagos(dataPagos);
      setDeudas(dataDeudas.filter((deuda) => Number(deuda.monto) > 0).sort((a, b) => b.id - a.id));
    } catch (e) {
      setError("No se pudieron obtener los cobros del pedido.");
    }
  };

  useEffect(() => {
    if (idPedido) cargar();
  }, [idPedido]);

  useEffect(() => {
    cargarMarcas().then(setMarcas);
  }, []);

  if (!idPedido) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        No se pudo identificar el pedido para consultar sus cobros.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mt: 1 }}
        action={
          <Button color="inherit" size="small" onClick={cargar}>
            Reintentar
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (pagos === null || deudas === null) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, p: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2">Cargando cobros...</Typography>
      </Stack>
    );
  }

  const pagosPedido = pagos.filter((pago) => !pago.id_gestion_cc);

  if (pagosPedido.length === 0 && deudas.length === 0) {
    return (
      <Seccion titulo="Cobros">
        <Typography variant="body2" color="text.secondary">
          Sin cobros registrados.
        </Typography>
      </Seccion>
    );
  }

  return (
    <Box>
      <Seccion
        titulo="Cobros del pedido"
        extra={
          pagosPedido.length ? (
            <Typography variant="body2" fontWeight={700}>
              Total {formatearMonto(sumar(pagosPedido))}
            </Typography>
          ) : null
        }
      >
        {pagosPedido.length ? (
          <TablaPagos pagos={pagosPedido} marcas={marcas} onVerComprobante={setComprobanteUid} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin cobros registrados.
          </Typography>
        )}
      </Seccion>

      {deudas.length ? (
        <Seccion titulo="Cuenta corriente">
          {deudas.map((deuda) => {
            const pagosDeuda = pagos.filter((pago) => pago.id_gestion_cc === deuda.id);
            const monto = Number(deuda.monto);
            const cobrado = sumar(pagosDeuda);
            const pendiente = Math.max(Math.round((monto - cobrado) * 100) / 100, 0);
            const cobrada = pendiente <= 0.01;
            return (
              <Box key={deuda.id} mb={1.5}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" mb={0.5}>
                  <Typography variant="body2" fontWeight="bold">
                    Deuda {formatearMonto(monto)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {deuda.created_at ? moment(deuda.created_at).format("DD-MM-YYYY HH:mm") : ""}
                    {deuda.usuario ? ` · ${deuda.usuario}` : ""}
                  </Typography>
                  <Chip
                    size="small"
                    color={cobrada ? "success" : "warning"}
                    label={cobrada ? "Cobrada" : `Pendiente ${formatearMonto(pendiente)}`}
                  />
                  {cobrado > 0 && !cobrada ? (
                    <Typography variant="body2" color="text.secondary">
                      Cobrado {formatearMonto(cobrado)}
                    </Typography>
                  ) : null}
                </Stack>
                {pagosDeuda.length ? (
                  <TablaPagos pagos={pagosDeuda} marcas={marcas} onVerComprobante={setComprobanteUid} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Sin cobros registrados.
                  </Typography>
                )}
              </Box>
            );
          })}
        </Seccion>
      ) : null}

      {comprobanteUid ? (
        <FileUploadEnvasado
          open={!!comprobanteUid}
          fileId={comprobanteUid}
          onClose={() => setComprobanteUid(null)}
          canEdit={false}
          title="Comprobantes de Transferencia"
        />
      ) : null}
    </Box>
  );
}

export default CobrosPedido;
